import { I18nPluralPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

interface PluralTranslationGroup {
  [count: string]: string;
}

/**
 * This translate service is similar to the ngx translate service except that you
 * add a count number as an additional argument and structure your translation
 * as a group of translations.
 *
 * Example translation file (en.json):
 * "candies": {
 *   "=0": "awww... no candy for me?!",
 *   "=1": "Yay! I have one piece of candy!",
 *   "other": "Super! I have {{ count }} pieces of candy!"
 * }
 *
 * Example translation (in a component which has injected this service as `this.pluralTranslateService`):
 * this.pluralTranslateService.instant('candies', 3);
 *
 * Adding extra interpolation arguments is available as a third argument:
 * this.pluralTranslateService.instant('candies', 3, { myArg: "Candy Land" });
 *
 * If no count is provided, this service will fall back to the core translate service functionality
 * (so you don't have to inject multiple translate services).
 */

@Injectable({
  providedIn: 'root'
})
export class PluralTranslateService {
  constructor(private translateService: TranslateService, private pluralPipe: I18nPluralPipe) {}

  translationGroup(key: string): PluralTranslationGroup {
    return this.translateService.getParsedResult(
      this.translateService.translations[this.translateService.currentLang],
      key
    );
  }

  translationForCount(key: string, count: number): string {
    return this.pluralPipe.transform(count, this.translationGroup(key));
  }

  instant(key: string, count?: number, interpolateParams?: Object): string {
    if (!isNaN(count)) {
      return this.translateService.parser.interpolate(this.translationForCount(key, count), {
        count,
        ...interpolateParams
      });
    } else {
      return this.translateService.instant(key, interpolateParams);
    }
  }
}
